import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { FaChartLine, FaBoxOpen, FaUsers, FaRupeeSign, FaTrophy } from 'react-icons/fa';
import './Analytics.css';

const Analytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30days');

  const COLORS = ['#6366f1', '#8b5cf6', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, productsRes, customersRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        user?.userType === 'seller' ? api.get('/analytics/products') : Promise.resolve({ data: {} }),
        user?.userType === 'seller' ? api.get('/analytics/customers') : Promise.resolve({ data: {} })
      ]);

      setAnalytics(analyticsRes.data.analytics);
      setProducts(productsRes.data.products || []);
      setCustomers(customersRes.data || null);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading analytics...</div>
      </Layout>
    );
  }

  if (!analytics) {
    return (
      <Layout>
        <div className="no-data">
          <h2>No analytics data available</h2>
          <p>Start selling or buying to see your analytics</p>
        </div>
      </Layout>
    );
  }

  const isSeller = user?.userType === 'seller';

  return (
    <Layout>
      <div className="analytics-page">
        <div className="page-header">
          <div>
            <h1>Analytics Dashboard</h1>
            <p>Track your {isSeller ? 'business' : 'purchase'} performance</p>
          </div>
          <div className="period-selector">
            <button
              className={period === '7days' ? 'active' : ''}
              onClick={() => setPeriod('7days')}
            >
              7 Days
            </button>
            <button
              className={period === '30days' ? 'active' : ''}
              onClick={() => setPeriod('30days')}
            >
              30 Days
            </button>
            <button
              className={period === '90days' ? 'active' : ''}
              onClick={() => setPeriod('90days')}
            >
              90 Days
            </button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="analytics-overview">
          <div className="analytics-card">
            <div className="card-icon" style={{ background: '#e0e7ff' }}>
              <FaRupeeSign style={{ color: '#6366f1' }} />
            </div>
            <div className="card-content">
              <h3>₹{analytics.overview?.[isSeller ? 'totalRevenue' : 'totalSpent']?.toLocaleString('en-IN') || 0}</h3>
              <p>{isSeller ? 'Total Revenue' : 'Total Spent'}</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon" style={{ background: '#d1fae5' }}>
              <FaChartLine style={{ color: '#10b981' }} />
            </div>
            <div className="card-content">
              <h3>{analytics.overview?.totalOrders || 0}</h3>
              <p>Total Orders</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon" style={{ background: '#fef3c7' }}>
              <FaRupeeSign style={{ color: '#f59e0b' }} />
            </div>
            <div className="card-content">
              <h3>₹{analytics.overview?.averageOrderValue || 0}</h3>
              <p>Average Order Value</p>
            </div>
          </div>

          {isSeller && (
            <div className="analytics-card">
              <div className="card-icon" style={{ background: '#dbeafe' }}>
                <FaBoxOpen style={{ color: '#3b82f6' }} />
              </div>
              <div className="card-content">
                <h3>{analytics.overview?.totalProducts || 0}</h3>
                <p>Total Products</p>
              </div>
            </div>
          )}
        </div>

        {/* Sales/Purchase Trend Chart */}
        {analytics.dailyData && analytics.dailyData.length > 0 && (
          <div className="analytics-section">
            <h2>{isSeller ? 'Sales' : 'Purchase'} Trend (Last 30 Days)</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analytics.dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(date) => new Date(date).toLocaleDateString('en-IN')}
                    formatter={(value) => ['₹' + value.toLocaleString('en-IN'), 'Revenue']}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#6366f1"
                    strokeWidth={3}
                    name="Revenue"
                    dot={{ fill: '#6366f1', r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="orders"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Orders"
                    dot={{ fill: '#10b981', r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Category Breakdown for Sellers */}
        {isSeller && analytics.categoryBreakdown && analytics.categoryBreakdown.length > 0 && (
          <div className="analytics-section">
            <h2>Revenue by Category</h2>
            <div className="chart-row">
              <div className="chart-container" style={{ width: '50%' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.categoryBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => entry.category}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="revenue"
                    >
                      {analytics.categoryBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => '₹' + value.toLocaleString('en-IN')} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-container" style={{ width: '50%' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.categoryBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip formatter={(value) => '₹' + value.toLocaleString('en-IN')} />
                    <Bar dataKey="revenue" fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Top Products for Sellers */}
        {isSeller && products.length > 0 && (
          <div className="analytics-section">
            <h2>
              <FaTrophy style={{ color: '#f59e0b', marginRight: '8px' }} />
              Top Performing Products
            </h2>
            <div className="products-table">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Views</th>
                    <th>Interests</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                    <th>Conv. Rate</th>
                    <th>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {products.slice(0, 10).map((product) => (
                    <tr
                      key={product.productId}
                      onClick={() => navigate(`/products/${product.productId}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {product.image && (
                            <img
                              src={product.image}
                              alt=""
                              style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }}
                            />
                          )}
                          <div>
                            <strong>{product.title}</strong>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{product.category}</div>
                          </div>
                        </div>
                      </td>
                      <td>{product.views}</td>
                      <td>{product.interests}</td>
                      <td>{product.orders}</td>
                      <td>₹{product.revenue.toLocaleString('en-IN')}</td>
                      <td>{product.conversionRate}%</td>
                      <td>⭐ {product.rating.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Customer Analytics for Sellers */}
        {isSeller && customers && (
          <div className="analytics-section">
            <h2>
              <FaUsers style={{ color: '#3b82f6', marginRight: '8px' }} />
              Customer Insights
            </h2>
            <div className="customers-summary">
              <div className="summary-card">
                <h3>{customers.summary?.totalCustomers || 0}</h3>
                <p>Total Customers</p>
              </div>
              <div className="summary-card">
                <h3>{customers.summary?.returningCustomers || 0}</h3>
                <p>Returning Customers</p>
              </div>
              <div className="summary-card">
                <h3>{customers.summary?.newCustomers || 0}</h3>
                <p>New (Last 30 Days)</p>
              </div>
              <div className="summary-card">
                <h3>{customers.summary?.retentionRate || 0}%</h3>
                <p>Retention Rate</p>
              </div>
            </div>

            {customers.customers && customers.customers.length > 0 && (
              <div className="products-table">
                <h3>Top Customers</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Contact</th>
                      <th>Total Orders</th>
                      <th>Total Spent</th>
                      <th>First Order</th>
                      <th>Last Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.customers.slice(0, 10).map((customer, index) => (
                      <tr key={index}>
                        <td>{customer.customer.businessName}</td>
                        <td>{customer.customer.phone}</td>
                        <td>{customer.totalOrders}</td>
                        <td>₹{customer.totalSpent.toLocaleString('en-IN')}</td>
                        <td>{new Date(customer.firstOrderDate).toLocaleDateString('en-IN')}</td>
                        <td>{new Date(customer.lastOrderDate).toLocaleDateString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Buyer-specific: Favorite Categories */}
        {!isSeller && analytics.favoriteCategories && analytics.favoriteCategories.length > 0 && (
          <div className="analytics-section">
            <h2>Your Favorite Categories</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.favoriteCategories}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip formatter={(value) => '₹' + value.toLocaleString('en-IN')} />
                  <Legend />
                  <Bar dataKey="spent" fill="#6366f1" name="Spending" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Buyer-specific: Top Sellers */}
        {!isSeller && analytics.topSellers && analytics.topSellers.length > 0 && (
          <div className="analytics-section">
            <h2>Your Top Sellers</h2>
            <div className="products-table">
              <table>
                <thead>
                  <tr>
                    <th>Seller</th>
                    <th>Orders</th>
                    <th>Total Spent</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.topSellers.map((seller, index) => (
                    <tr key={index}>
                      <td>{seller.name}</td>
                      <td>{seller.orders}</td>
                      <td>₹{seller.spent.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;
